// Robust, user-friendly alphabetical sorting (case-insensitive, natural numeric ordering).
const _alphaCollator = (typeof Intl !== "undefined" && Intl.Collator)
  ? new Intl.Collator(undefined, { numeric: true, sensitivity: "base" })
  : null;

export function normSortString(x) {
  try {
    return String(x ?? "")
      .normalize("NFKD")
      .replace(/[\u200B-\u200D\uFEFF]/g, "") // zero-width chars
      .replace(/\s+/g, " ")
      .trim();
  } catch {
    return String(x ?? "").replace(/\s+/g, " ").trim();
  }
}

export function alphaCompare(a, b) {
  const aa = normSortString(a);
  const bb = normSortString(b);
  if (_alphaCollator) return _alphaCollator.compare(aa, bb);
  return aa.localeCompare(bb, undefined, { sensitivity: "base" });
}

export function normalizeRpcEndpoint(v) {
  return String(v || "").trim().replace(/\/+$/, "");
}

export function uniq(arr) {
  const out = [];
  const seen = new Set();
  for (const x of arr || []) {
    const k = String(x || "");
    if (!k) continue;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
}

export function wrapLine(str, maxLen = 95) {
  const s = String(str ?? "");
  if (s.length <= maxLen) return [s];
  const out = [];
  let cur = s;
  while (cur.length > maxLen) {
    let cut = cur.lastIndexOf(" ", maxLen);
    if (cut < 40) cut = maxLen;
    out.push(cur.slice(0, cut));
    cur = cur.slice(cut).trimStart();
  }
  if (cur) out.push(cur);
  return out;
}

export function pdfEscapeLiteral(str) {
  // Escape backslash and parentheses for PDF literal strings.
  return String(str ?? "")
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");
}

export function encodeLatin1(str) {
  const s = String(str ?? "");
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) {
    const cp = s.charCodeAt(i);
    out[i] = cp <= 255 ? cp : 63; // '?' for unsupported chars
  }
  return out;
}

export function concatBytes(parts) {
  const total = parts.reduce((sum, p) => sum + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

export function pick(obj, key, fallback = "") {
  const v = obj?.[key];
  return v === undefined || v === null ? fallback : v;
}

export function asArrayOfNames(v) {
  if (Array.isArray(v)) return v.map((x) => String(x?.actor ?? x?.value ?? x ?? "").trim()).filter(Boolean);
  if (typeof v === "string") {
    try {
      const parsed = JSON.parse(v);
      if (Array.isArray(parsed)) return parsed.map((x) => String(x || "").trim()).filter(Boolean);
    } catch {
      // fallthrough
    }
    return v.split(/[\s,]+/).map((x) => String(x || "").trim()).filter(Boolean);
  }
  return [];
}
